import {
  createContext, useCallback, useContext, useMemo, useReducer,
} from 'react';
import { getRefCompany, getRefUser, getRefUsersList } from '../../utils/firestore';
import { AuthContext } from '../AuthProvider';
import firestoreErrors from '../../utils/firestoreErrors';
import { FirebaseError } from 'firebase/app';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';
import { error as errorLabels } from '../../label';
import getRequestMeta from '../../utils/functions/generateMeta';
import { ip } from '../../utils/functions/urls';
import { Group } from '../../types';


interface GroupsState {
  groups: Group[];
  isLoading: boolean;
  started: boolean;
}

interface GroupsContextValue {
  state: GroupsState;
  groupsAPI: {
    getGroups: () => Promise<{ error: boolean; msg: string; raw?: string }>;
    createGroup: (group: Omit<Group, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>) => Promise<{ error: boolean; msg: string; raw?: string }>;
    updateGroup: (id: string, group: Partial<Group>) => Promise<{ error: boolean; msg: string; raw?: string }>;
    deleteGroup: (id: string) => Promise<{ error: boolean; msg: string; raw?: string }>;
  };
}

const initialState: GroupsState = {
  groups: [],
  isLoading: false,
  started: false,
};

export const GroupsContext = createContext<GroupsContextValue>({} as GroupsContextValue);;

type Action =
  | { type: 'setGroups'; groups: Group[] }
  | { type: 'setLoading'; isLoading: boolean }
  | { type: 'clear' };

const reducer = (state: GroupsState, action: Action): GroupsState => {
  switch (action.type) {
    case 'setGroups':
      return {
        ...state,
        groups: action.groups,
        started: true,
        isLoading: false,
      };
    case 'setLoading':
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case 'clear':
      return initialState;
    default:
      return state;
  }
};

export function GroupsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { currentUser, user } = useContext(AuthContext);

  const setLoading = useCallback((l = true) => dispatch({ type: 'setLoading', isLoading: l }), []);

  const getGroups = useCallback(async () => {
    try {
      setLoading(true);
      const companyDoc = await getRefCompany(user.company).get();
      const companyData = await companyDoc.data();
      const groups = companyData?.groups || [];
      dispatch({ type: 'setGroups', groups: groups.sort((a: Group, b: Group) => a.name.localeCompare(b.name)) });
      setLoading(false);
      return {
        error: false,
        msg: '',
      };
    } catch (error) {
      const err = error as FirebaseError;
      setLoading(false);
      return {
        error: true,
        msg: 'Erro ao buscar grupos',
        raw: firestoreErrors(err.code),
      };
    }
  }, [user]);

  const createGroup = useCallback(async (group: Omit<Group, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>) => {
    try {
      setLoading(true);
      const companyRef = getRefCompany(user.company);
      const timestamp = new Date().toISOString();
      const newGroup = {
        ...group,
        id: uuidv4(),
        created_at: timestamp,
        updated_at: timestamp,
        created_by: user.uid,
        updated_by: user.uid,
      };
      const companyDoc = await companyRef.get();
      const companyData = await companyDoc.data();

      const groups = companyData?.groups || [];
      const groupExists = groups.some((g: Group) => g.name === newGroup.name);
      if (groupExists) {
        return {
          error: true,
          msg: `Grupo "${newGroup.name}" já existe`,
        };
      }
      groups.push(newGroup);
      await companyRef.set({
        groups,
      }, { merge: true });

      await getGroups();
      setLoading(false);
      return { error: false, msg: '' };
    } catch (error) {
      console.log(error);
      const err = error as FirebaseError;
      setLoading(false);
      return {
        error: true,
        msg: 'Erro ao criar grupo',
        raw: firestoreErrors(err.code),
      };
    }
  }, [user, getGroups]);

  const updateGroup = useCallback(async (id: string, groupData: Partial<Group>) => {
    try {
      setLoading(true);
      const companyRef = getRefCompany(user.company);
      const companyDoc = await companyRef.get();
      const companyData = await companyDoc.data();
      const groups = companyData?.groups || [];

      const timestamp = new Date().toISOString();
      const updatedGroups = groups.map((g: Group) => {
        if (g.id === id) {
          return {
            ...g,
            ...groupData,
            updated_at: timestamp,
            updated_by: user.uid,
          };
        }
        return g;
      });

      await companyRef.update({ groups: updatedGroups });
      await getGroups();
      setLoading(false);
      return { error: false, msg: '' };
    } catch (error) {
      setLoading(false);
      const err = error as FirebaseError;
      return {
        error: true,
        msg: 'Erro ao atualizar grupo',
        raw: firestoreErrors(err.code),
      };
    }
  }, [user, getGroups]);

  const deleteGroup = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
      };
      const resFetch = await fetch(`${ip}/manage/groups/${id}`, opt);
      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      setLoading(false);
      if (resFetch.status !== 200) {
        return { error: true, msg: errorLabels.groupsProvider.deleteGroup, raw: json.error };
      }
      return {
        error: false, msg: '', raw: '', res: json.info,
      };
    } catch (error) {
      setLoading(false);
      const err = error as FirebaseError;
      return {
        error: true,
        msg: 'Erro ao deletar grupo',
        raw: firestoreErrors(err.code),
      };
    }
  }, [user, getGroups]);

  const groupsAPI = useMemo(() => ({
    getGroups,
    createGroup,
    updateGroup,
    deleteGroup,
  }), [getGroups, createGroup, updateGroup, deleteGroup]);

  return (
    <GroupsContext.Provider value={{ state, groupsAPI }}>
      {children}
    </GroupsContext.Provider>
  );
}
